import { useContainer } from "features/container/Feature";
import { useEditor } from "features/editor/Feature";
import { usePitcher } from "features/utils/usePitcher";
import {
  hasBranchWriteAccess,
  hasSandboxPermission,
} from "isomorphic/permission";
import { Button, Stack, Dialog } from "prism-react";
import { router } from "router";

import { GlobalModal } from "components/GlobalModal";
import { useAnalytics } from "hooks/useAnalytics";
import { paths } from "utils/router";

/**
 * This modal shows if we got disconnected and it was NOT a clean disconnect. A clean disconnect means
 * the VM notified that it was hibernating. Before showing the modal there will always be attempts to
 * reconnect first.
 */
export const DisconnectedModal = ({
  wasNotFound,
}: {
  wasNotFound: boolean;
}) => {
  const pitcher = usePitcher();
  const [, dispatch] = useContainer();
  const project = useEditor();
  const track = useAnalytics();

  if (wasNotFound && project.type === "sandbox") {
    return (
      <GlobalModal maxWidth={500} open>
        <Dialog.Title>Devbox not found</Dialog.Title>
        <Dialog.Description>
          We are unable to reconnect you as the Devbox was not found.
        </Dialog.Description>
        <Stack css={{ justifyContent: "flex-end" }} horizontal>
          <Button
            onClick={() => {
              window.location.href = paths.dashboard.recent();
            }}
            variant="primary"
            autoFocus
          >
            Dashboard
          </Button>
        </Stack>
      </GlobalModal>
    );
  }

  if (wasNotFound && project.type === "branch") {
    return (
      <GlobalModal maxWidth={500} open>
        <Dialog.Title>
          {project.type === "branch" ? "Branch not found" : "Devbox not found"}
        </Dialog.Title>
        <Dialog.Description>
          We are unable to reconnect you as the branch was not found. Most
          likely this branch was deleted from the remote git server after it was
          merged.
        </Dialog.Description>
        <Stack css={{ justifyContent: "flex-end" }} horizontal>
          <Button
            onClick={() => {
              window.location.href = paths.dashboard.recent();
            }}
            autoFocus
          >
            Open Dashboard
          </Button>

          <Button
            onClick={() => {
              window.location.href =
                window.location.origin +
                router.url("github", {
                  owner: project.owner,
                  repo: project.repo,
                  branch: project.defaultBranchName,
                }) +
                (project.workspace
                  ? `?workspaceId=${project.workspace.id}`
                  : "");
            }}
            variant="primary"
          >
            Open {project.defaultBranchName} branch
          </Button>
        </Stack>
      </GlobalModal>
    );
  }

  return (
    <GlobalModal maxWidth={500} open>
      <Dialog.Title>Lost Connection</Dialog.Title>
      <Dialog.Description>
        We are unable to reconnect you. Please check that you are online before
        trying again. If the problem persists, it means the development
        environment has reached an error state and you can try restarting it.
        Don't worry, your work will still be here when you get back.
      </Dialog.Description>
      <Stack css={{ justifyContent: "flex-end" }} horizontal>
        <Button
          disabled={
            !(project.type === "branch"
              ? hasBranchWriteAccess(project.authorization)
              : hasSandboxPermission(project.authorization, "write_project"))
          }
          onClick={() => {
            dispatch({ type: "RESTART_CONTAINER" });
          }}
          autoFocus
        >
          Restart
        </Button>

        <Button
          onClick={() => {
            track("manual_reconnect", {
              event_source: "editor",
            });
            pitcher.reconnect().catch(() => {
              // We do not care if this fails as it is event driven what the final state becomes
            });
          }}
          variant="primary"
        >
          Reconnect
        </Button>
      </Stack>
    </GlobalModal>
  );
};
